// File: src/components/AdminView.js
// Main admin panel component with tabbed interface

import React, { useState, useEffect } from 'react';
import { Trophy, LogOut, Users, Key, Shield, Settings, UserCircle, Calendar, BarChart3, Download, HelpCircle } from 'lucide-react';
import SeriesManagement from './SeriesManagement';
import DriverManagement from './DriverManagement';
import RaceManagement from './RaceManagement';
import EventManagement from './EventManagement';
import TabbedStandingsView from './TabbedStandingsView';
import ClassTabs from './ClassTabs';
import SeriesClassManagement from './SeriesClassManagement';
import HelpView from './HelpView';
import ThemeToggle from './ThemeToggle';
import {
  LanguageSelect, CountrySelect, HEADER_BUTTON, HEADER_BUTTON_DANGER
} from './HeaderControls';
import { usePermissions } from '../hooks/usePermissions';
import { useLanguage } from '../contexts/LanguageContext';
import { useCountry } from '../contexts/CountryContext';
import { useCountryName } from '../utils/countryNames';
import { useRoleLabel } from '../utils/roles';
import { formatSeriesNameWithYear, isSeriesActiveInYear, getAvailableYears, getCurrentYear, getTodayISO } from '../utils/utils';
import { handleUnauthorized } from '../utils/session';
import { API_BASE_URL } from '../App';
import CountryOptions from './CountryOptions';
import { getCountry } from '../data/countries';

const ROLE_TEXT_CLASSES = {
  admin: 'text-accent',
  editor: 'text-blue-700 dark:text-blue-400',
  organizer: 'text-green-700 dark:text-green-400',
  competitor: 'text-ink-muted'
};

function AdminView({
  series,
  selectedSeries,
  setSelectedSeries,
  selectedClass,
  setSelectedClass,
  currentSeries,
  token,
  userRole,
  username,
  handleLogout,
  setShowUserManagement,
  setShowPasswordChange,
  loadSeries
}) {
  const permissions = usePermissions(userRole);
  const { t, exportLanguage, changeExportLanguage, availableLanguages } = useLanguage();
  const roleLabel = useRoleLabel();
  const { currentCountry } = useCountry();
  const countryName = useCountryName();
  const [activeTab, setActiveTab] = useState('settings');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [selectedSeriesIds, setSelectedSeriesIds] = useState([]);
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());
  const [exportYear, setExportYear] = useState(getCurrentYear());
  const [exportCountry, setExportCountry] = useState(currentCountry.code);
  const [showHelp, setShowHelp] = useState(false);

  // Filter series by selected year and country
  const filteredSeries = series.filter(s =>
    isSeriesActiveInYear(s, selectedYear) && s.country === currentCountry.code
  );
  const availableYears = getAvailableYears(series);

  // Drivers, races and standings all belong to a class. The fallback to the
  // first one means a series that has just been selected shows something
  // immediately rather than waiting for a click.
  const classes = currentSeries?.classes || [];
  const currentClass = classes.find(c => c.id === selectedClass) || classes[0];

  // Filter series for export by export year and export country
  const exportFilteredSeries = series.filter(s =>
    isSeriesActiveInYear(s, exportYear) && s.country === exportCountry
  );

  // Auto-select first series in filtered list if current selection is not visible
  useEffect(() => {
    if (filteredSeries.length > 0) {
      const isCurrentSeriesVisible = filteredSeries.some(s => s.id === selectedSeries);
      if (!isCurrentSeriesVisible) {
        setSelectedSeries(filteredSeries[0].id);
      }
    }
  }, [selectedYear, filteredSeries, selectedSeries, setSelectedSeries]);

  const tabs = [
    { id: 'settings', label: t('seriesSettings'), icon: Settings },
    { id: 'drivers', label: t('drivers'), icon: UserCircle },
    { id: 'races', label: t('races'), icon: Calendar },
    { id: 'standings', label: t('standings'), icon: BarChart3 }
  ];

  const toggleSeriesSelection = (seriesId) => {
    setSelectedSeriesIds(prev => {
      if (prev.includes(seriesId)) {
        return prev.filter(id => id !== seriesId);
      } else {
        return [...prev, seriesId];
      }
    });
  };

  const handleExportChampionship = async () => {
    if (selectedSeriesIds.length === 0) {
      alert(t('pleaseSelectAtLeastOneSeries'));
      return;
    }

    setShowExportDialog(false);

    try {
      const response = await fetch(`${API_BASE_URL}/export/championship`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          seriesIds: selectedSeriesIds,
          language: exportLanguage
        })
      });

      if (handleUnauthorized(response)) {
        return;
      }

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `Championship_Export_${getTodayISO()}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Reset selection
      setSelectedSeriesIds([]);

    } catch (error) {
      console.error('Export error:', error);
      alert(t('failedToExportChampionship'));
    }
  };

  // Persist a single series setting and surface any failure to the user, so a
  // failed save (network error, server error, expired session) is never silent.
  const saveSeriesSetting = async (updates) => {
    try {
      const res = await fetch(`${API_BASE_URL}/series/${currentSeries.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
      if (handleUnauthorized(res)) return;
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(t('saveFailed') + (data.error ? `: ${data.error}` : ''));
        return;
      }
      loadSeries();
    } catch (error) {
      console.error('Error saving series setting:', error);
      alert(t('connectionError'));
    }
  };

  return (
    <div className="app-shell p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-3 mb-8">
          <div className="flex items-center gap-3">
            <Trophy className="w-10 h-10 text-accent" />
            <div>
              <h1 className="text-4xl font-bold text-ink">{t('adminPanel')}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-ink-muted text-sm">{t('login')}: {username}</span>
                <span className="text-ink-muted">•</span>
                <span className={`text-sm font-semibold flex items-center gap-1 ${ROLE_TEXT_CLASSES[userRole] || ROLE_TEXT_CLASSES.competitor}`}>
                  <Shield className="w-4 h-4" />
                  {roleLabel(userRole)}
                </span>
              </div>
            </div>
          </div>
          {/* Everything on the row. This was a hamburger holding nine items,
              including a country popover nested a second level down - three
              clicks to change country, on a screen with room for all of it. */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setShowHelp(true)}
              className={HEADER_BUTTON}
              title={t('help')}
              aria-label={t('help')}
            >
              <HelpCircle className="w-4 h-4" />
            </button>

            {/* Labels collapse to their icons on a phone. Nine controls with
                full labels stack six rows deep before the page even starts. */}
            <button
              onClick={() => setShowExportDialog(true)}
              className={HEADER_BUTTON}
              title={t('exportExcel')}
              aria-label={t('exportExcel')}
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">{t('exportExcel')}</span>
            </button>

            {permissions.canEdit('users') && (
              <button
                onClick={() => setShowUserManagement(true)}
                className={HEADER_BUTTON}
                title={t('manageUsers')}
                aria-label={t('manageUsers')}
              >
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">{t('manageUsers')}</span>
              </button>
            )}

            {/* Icon only: the key is unambiguous and the label is long enough to
                push the row onto another line on its own. */}
            <button
              onClick={() => setShowPasswordChange(true)}
              className={HEADER_BUTTON}
              title={t('changePassword')}
              aria-label={t('changePassword')}
            >
              <Key className="w-4 h-4" />
            </button>

            <LanguageSelect />
            <CountrySelect />

            <button
              onClick={handleLogout}
              className={HEADER_BUTTON_DANGER}
              title={t('logout')}
              aria-label={t('logout')}
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{t('logout')}</span>
            </button>

            <ThemeToggle />
          </div>
        </div>

        {/* Year Selector */}
        <div className="bg-paper rounded-lg p-6 mb-6 border border-line">
          <div className="max-w-xs">
            <label className="block text-ink-muted mb-2 font-semibold">{t('selectYear')}:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          {filteredSeries.length === 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-500/20 border border-yellow-500 text-yellow-800 dark:text-yellow-200 px-4 py-3 rounded mt-4">
              {t('noSeriesForYear')} {selectedYear} ({countryName(currentCountry)})
            </div>
          )}
        </div>

        {/* Events - independent of the series selection below, because
            organizers have no series rights at all. */}
        {permissions.canManageEvents() && (
          <div className="bg-paper rounded-lg border border-line p-6 mb-6">
            <EventManagement token={token} userRole={userRole} />
          </div>
        )}

        {/* Series Selector */}
        {permissions.canManageSeries() && (
        <SeriesManagement
          series={filteredSeries}
          selectedSeries={selectedSeries}
          setSelectedSeries={setSelectedSeries}
          currentSeries={currentSeries}
          token={token}
          loadSeries={loadSeries}
        />
        )}

        {/* Which class of the series everything below is about. Hidden for a
            series with one class, which is every series that predates them. */}
        {permissions.canManageSeries() && currentSeries && (
          <div className="mt-6">
            <ClassTabs
              classes={classes}
              selectedClassId={currentClass?.id}
              onSelect={setSelectedClass}
            />
          </div>
        )}

        {/* Tabbed Interface */}
        {permissions.canManageSeries() && currentSeries && (
          <div className="bg-paper rounded-lg border border-line mt-2">
            {/* Tab Navigation */}
            <div className="border-b border-line">
              <div className="flex overflow-x-auto">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-6 py-4 font-semibold transition-colors whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'bg-paper-alt text-ink border-b-2 border-accent'
                          : 'text-ink-muted hover:text-ink hover:bg-paper-alt'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {/* Series Settings Tab */}
              {activeTab === 'settings' && (
                <div>
                  <h2 className="text-2xl font-bold text-ink mb-6">{t('seriesSettings')}</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Total Races */}
                    <div className="bg-paper-alt p-4 rounded-lg">
                      <label className="block text-ink-muted mb-2 font-semibold">{t('totalRaces')}</label>
                      <input
                        type="number"
                        min="0"
                        value={currentSeries.total_races || 0}
                        onChange={(e) => saveSeriesSetting({ totalRaces: parseInt(e.target.value) || 0 })}
                        className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                      />
                      <p className="text-ink-muted text-sm mt-2">{t('totalRacesDescription')}</p>
                    </div>

                    {/* Races Counted */}
                    <div className="bg-paper-alt p-4 rounded-lg">
                      <label className="block text-ink-muted mb-2 font-semibold">{t('racesCounted')}</label>
                      <input
                        type="number"
                        min="0"
                        value={currentSeries.races_counted || 0}
                        onChange={(e) => saveSeriesSetting({ racesCounted: parseInt(e.target.value) || 0 })}
                        className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                      />
                      <p className="text-ink-muted text-sm mt-2">{t('racesCountedDescription')}</p>
                    </div>

                    {/* Qualification Points */}
                    <div className="bg-paper-alt p-4 rounded-lg">
                      <label className="block text-ink-muted mb-2 font-semibold">{t('calculateQualificationPoints')}</label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={currentSeries.calculate_qualification_points || false}
                          onChange={(e) => saveSeriesSetting({ calculateQualificationPoints: e.target.checked })}
                          className="w-5 h-5"
                        />
                        <span className="text-ink">{t('enable')}</span>
                      </label>
                      <p className="text-ink-muted text-sm mt-2">{t('qualificationPointsDescription')}</p>
                    </div>

                    {/* Qualification Master */}
                    {currentSeries.calculate_qualification_points && (
                      <div className="bg-paper-alt p-4 rounded-lg">
                        <label className="block text-ink-muted mb-2 font-semibold">{t('qualificationMaster')}</label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={currentSeries.qualification_master || false}
                            onChange={(e) => saveSeriesSetting({ qualificationMaster: e.target.checked })}
                            className="w-5 h-5"
                          />
                          <span className="text-ink">{t('enable')}</span>
                        </label>
                        <p className="text-ink-muted text-sm mt-2">{t('qualificationMasterDescription')}</p>
                      </div>
                    )}

                    {/* Junior Championship */}
                    <div className="bg-paper-alt p-4 rounded-lg">
                      <label className="block text-ink-muted mb-2 font-semibold">{t('juniorChampionship')}</label>
                      <label className="flex items-center gap-2 cursor-pointer mb-3">
                        <input
                          type="checkbox"
                          checked={currentSeries.junior_championship || false}
                          onChange={(e) => saveSeriesSetting({ juniorChampionship: e.target.checked })}
                          className="w-5 h-5"
                        />
                        <span className="text-ink">{t('enable')}</span>
                      </label>
                      {currentSeries.junior_championship && (
                        <div>
                          <label className="block text-ink-muted text-sm mb-1">{t('ageLimit')}</label>
                          <input
                            type="number"
                            min="10"
                            max="30"
                            value={currentSeries.junior_age_limit || 18}
                            onChange={(e) => saveSeriesSetting({ juniorAgeLimit: parseInt(e.target.value) || 18 })}
                            className="w-full px-3 py-1 bg-paper-alt border border-line rounded text-ink text-sm"
                          />
                          <p className="text-ink-muted text-xs mt-1">🧒 {t('drivers')} {currentSeries.junior_age_limit || 18} {t('yearsAndUnder')}</p>
                        </div>
                      )}
                    </div>

                    {/* Senior Championship */}
                    <div className="bg-paper-alt p-4 rounded-lg">
                      <label className="block text-ink-muted mb-2 font-semibold">{t('seniorChampionship')}</label>
                      <label className="flex items-center gap-2 cursor-pointer mb-3">
                        <input
                          type="checkbox"
                          checked={currentSeries.senior_championship || false}
                          onChange={(e) => saveSeriesSetting({ seniorChampionship: e.target.checked })}
                          className="w-5 h-5"
                        />
                        <span className="text-ink">{t('enable')}</span>
                      </label>
                      {currentSeries.senior_championship && (
                        <div>
                          <label className="block text-ink-muted text-sm mb-1">{t('ageLimit')}</label>
                          <input
                            type="number"
                            min="18"
                            max="99"
                            value={currentSeries.senior_age_limit || 40}
                            onChange={(e) => saveSeriesSetting({ seniorAgeLimit: parseInt(e.target.value) || 40 })}
                            className="w-full px-3 py-1 bg-paper-alt border border-line rounded text-ink text-sm"
                          />
                          <p className="text-ink-muted text-xs mt-1">👴 {t('drivers')} {currentSeries.senior_age_limit || 40}{t('plusYearsOld')}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-500/20 border border-blue-500 rounded-lg">
                    <p className="text-blue-800 dark:text-blue-200 text-sm">
                      <strong>💡 {t('tip')}</strong> {t('changesAutoSaved')}
                    </p>
                  </div>

                  {/* The classes this series runs. Everything above is the
                      series default; a class may bend any of it. */}
                  <SeriesClassManagement
                    series={currentSeries}
                    token={token}
                    onChange={loadSeries}
                  />
                </div>
              )}

              {/* Drivers Tab */}
              {activeTab === 'drivers' && (
                <DriverManagement
                  currentSeries={currentSeries}
                  currentClass={currentClass}
                  token={token}
                  userRole={userRole}
                  loadSeries={loadSeries}
                />
              )}

              {/* Races Tab */}
              {activeTab === 'races' && (
                <RaceManagement
                  currentClass={currentClass}
                  token={token}
                  userRole={userRole}
                  loadSeries={loadSeries}
                />
              )}

              {/* Standings Tab */}
              {activeTab === 'standings' && (
                <TabbedStandingsView seriesClass={currentClass} isPublic={false} />
              )}
            </div>
          </div>
        )}

        {/* Export Dialog */}
        {showExportDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-paper rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl font-bold text-ink mb-4 flex items-center gap-2">
                <Download className="w-6 h-6 text-green-700 dark:text-green-400" />
                {t('exportChampionships')}
              </h3>

              {/* Export Year Selector */}
              <div className="mb-4 p-3 bg-paper-alt rounded-lg">
                <label className="block text-ink-muted text-sm font-semibold mb-2">{t('selectYear')}</label>
                <select
                  value={exportYear}
                  onChange={(e) => {
                    setExportYear(parseInt(e.target.value));
                    setSelectedSeriesIds([]); // Clear selections when year changes
                  }}
                  className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                >
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              {/* Export Country Selector */}
              <div className="mb-4 p-3 bg-paper-alt rounded-lg">
                <label className="block text-ink-muted text-sm font-semibold mb-2">{t('country')}</label>
                <select
                  value={exportCountry}
                  onChange={(e) => {
                    setExportCountry(e.target.value);
                    setSelectedSeriesIds([]); // Clear selections when country changes
                  }}
                  className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                >
                  <CountryOptions />
                </select>
              </div>

              {/* Export Language Selector */}
              <div className="mb-4 p-3 bg-paper-alt rounded-lg">
                <label className="block text-ink-muted text-sm font-semibold mb-2">{t('exportLanguage')}</label>
                <div className="flex gap-2">
                  {availableLanguages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => changeExportLanguage(lang.code)}
                      className={`flex-1 px-3 py-2 rounded transition-colors font-semibold ${
                        exportLanguage === lang.code
                          ? 'bg-green-600 text-white'
                          : 'bg-paper-alt text-ink-muted hover:opacity-80'
                      }`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              </div>

              <p className="text-ink-muted mb-2">{t('selectSeriesToExport')}</p>
              <p className="text-ink-muted text-sm mb-4">
                {selectedSeriesIds.length > 0 ? `${selectedSeriesIds.length} ${t('selected')}` : t('selectAtLeastOne')}
              </p>

              <div className="space-y-2 max-h-96 overflow-y-auto mb-4">
                {exportFilteredSeries.length === 0 ? (
                  <p className="text-ink-muted text-center py-4">
                    {t('noSeriesForYear')} {exportYear} ({countryName(getCountry(exportCountry))})
                  </p>
                ) : (
                  exportFilteredSeries.map(s => (
                    <label
                      key={s.id}
                      className="flex items-center gap-3 bg-paper-alt hover:opacity-80 px-4 py-3 rounded-lg cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSeriesIds.includes(s.id)}
                        onChange={() => toggleSeriesSelection(s.id)}
                        className="w-5 h-5 rounded border-line text-green-600 focus:ring-green-500 focus:ring-offset-paper"
                      />
                      <span className="font-semibold text-ink flex-1">
                        {formatSeriesNameWithYear(s.name, s.start_year, s.end_year)}
                      </span>
                    </label>
                  ))
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleExportChampionship}
                  disabled={selectedSeriesIds.length === 0}
                  className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-paper-alt disabled:text-ink-muted disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  {t('export')} ({selectedSeriesIds.length})
                </button>
                <button
                  onClick={() => {
                    setShowExportDialog(false);
                    setSelectedSeriesIds([]);
                  }}
                  className="bg-paper-alt hover:opacity-80 text-ink px-4 py-2 rounded-lg font-semibold"
                >
                  {t('cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Help View */}
        {showHelp && <HelpView onClose={() => setShowHelp(false)} isAdmin={true} />}
      </div>
    </div>
  );
}

export default AdminView;
